import type {
  FetchQueryOptions,
  InvalidateOptions,
  InvalidateQueryFilters,
  SetDataOptions,
  UseQueryOptions,
} from '@tanstack/react-query';
import { useQueryClient } from '@tanstack/react-query';

// Types -------------------------------------------------------------------------

/**
 * Typing for a function that returns a query key based on the provided parameters.
 * @typeParam TParameters - The type of the parameters used to generate the query key.
 * @param parameters - The parameters used to generate the query key.
 * @returns A query key based on the provided parameters.
 * @example
 * ```ts
 * const getKey = (parameters?: Partial<BenefitNotificationParameters>) => {
 *   if (isEmpty(parameters)) return [QUERY_KEY_BENEFIT_NOTIFICATIONS];
 *  return [QUERY_KEY_BENEFIT_NOTIFICATIONS, parameters];
 * };
 * ```
 */
export type TGetKey<TParameters> = (
  parameters?: Partial<TParameters>
) =>
  | string[]
  | (string | Record<string, unknown>)[]
  | readonly (string | Record<string, unknown>)[];

/**
 * Typing for the options used when creating a useQueryBase hook.
 * @typeParam TData - The type of the data returned by the query function.
 */
export type TUseQueryOptions<TData, TError = unknown> = Omit<
  UseQueryOptions<TData, TError>,
  'queryKey' | 'queryFn'
>;

// Invalidation------------------------------------------------------------------

/**
 * Creates a useInvalidateBase hook that invalidates a query based on the provided parameters.
 * Uses tanstack/react-query's invalidateQueries from useQueryClient hook.
 * @typeParam TParameters - The type of the parameters used to generate the query key.
 * @param getKey - A function that returns a query key based on the provided parameters.
 * @returns A useInvalidateBase hook that invalidates a query based on the provided parameters.
 * @example
 * ```ts
 * const getKey = (parameters?: Partial<BenefitNotificationParameters>={}) => {
 *    if (isEmpty(parameters)) return [QUERY_KEY_BENEFIT_NOTIFICATIONS];
 *    return [QUERY_KEY_BENEFIT_NOTIFICATIONS, parameters];
 * };
 *
 * const useInvalidateBenefitNotifications = createInvalidateHook<BenefitNotificationParameters>(getKey);
 *
 * useInvalidateBenefitNotifications(options);
 * ```
 * @see {@link useInvalidateBase}
 */
export const createInvalidateHook = <TParameters = undefined>(
  getKey: TGetKey<TParameters>
) => {
  return (options?: InvalidateOptions) =>
    useInvalidateBase<TParameters>(getKey, options);
};

/**
 * Invalidates a query based on the provided parameters. It uses tanstack/react-query's useQueryClient hook.
 * @typeParam TParameters - The type of the parameters used to generate the query key.
 * @param getKey - A function that returns a query key based on the provided parameters.
 * @param options - The options to use when invalidating the query.
 * @returns A function that invalidates a query based on the provided parameters.
 * @example
 * ```ts
 * const getKey = (parameters?: Partial<BenefitNotificationParameters>={}) => {
 *    if (isEmpty(parameters)) return [QUERY_KEY_BENEFIT_NOTIFICATIONS];
 *    return [QUERY_KEY_BENEFIT_NOTIFICATIONS, parameters];
 * };
 *
 * const invalidateBenefitNotifications = (options?: InvalidateOptions) => useInvalidateBase<BenefitNotificationParameters>(getKey, options);
 * ```
 */
export const useInvalidateBase = <TParameters = undefined>(
  getKey: TGetKey<TParameters>,
  options?: InvalidateOptions
) => {
  const queryClient = useQueryClient();

  return (
    parameters?: Partial<TParameters>,
    filters?: InvalidateQueryFilters
  ) =>
    queryClient.invalidateQueries(
      {
        ...filters,
        queryKey: getKey(parameters),
      },
      options
    );
};

// Prefetching ------------------------------------------------------------------
/**
 * Creates a usePrefetchBase hook that prefetches a query based on the provided parameters.
 * Uses tanstack/react-query's prefetchQuery from useQueryClient hook.
 * @typeParam TParameters - The type of the parameters used to generate the query key.
 * @typeParam TData - The type of the data returned by the query function.
 * @param getKey - A function that returns a query key based on the provided parameters.
 * @param queryFunction - The function that fetches the data for the query.
 * @returns A usePrefetchBase hook that prefetches a query based on the provided parameters.
 * @example
 * ```ts
 * const getKey = (parameters?: Partial<BenefitNotificationParameters>) => {
 *    if (isEmpty(parameters)) return [QUERY_KEY_BENEFIT_NOTIFICATIONS];
 *    return [QUERY_KEY_BENEFIT_NOTIFICATIONS, parameters];
 * };
 *
 * const usePrefetchBenefitNotifications = createPrefetchHook<BenefitNotificationParameters, BenefitNotificationsResponse>(getKey, getBenefitNotifications);
 *
 * usePrefetchBenefitNotifications(options);
 * ```
 * @see {@link usePrefetchBase}
 */
export const createPrefetchHook = <TParameters, TData>(
  getKey: TGetKey<TParameters>,
  queryFunction: (parameters?: TParameters) => Promise<TData>
) => {
  return (options?: FetchQueryOptions) =>
    usePrefetchBase<TParameters, TData>(getKey, queryFunction, options);
};

/**
 * Prefetches a query based on the provided parameters. It uses tanstack/react-query's useQueryClient hook.
 * @typeParam TParameters - The type of the parameters used to generate the query key.
 * @typeParam TData - The type of the data returned by the query function.
 * @param getKey - A function that returns a query key based on the provided parameters.
 * @param queryFunction - The function that fetches the data for the query.
 * @param options - The options to use when prefetching the query.
 * @returns A function that prefetches a query based on the provided parameters.
 * @example
 * ```ts
 * const getKey = (parameters?: Partial<BenefitNotificationParameters>={}) => {
 *    if (isEmpty(parameters)) return [QUERY_KEY_BENEFIT_NOTIFICATIONS];
 *    return [QUERY_KEY_BENEFIT_NOTIFICATIONS, parameters];
 * };
 *
 * const prefetchBenefitNotifications = (options?: FetchQueryOptions) => usePrefetchBase<BenefitNotificationParameters, BenefitNotificationsResponse>(getKey, getBenefitNotifications, options);
 * ```
 */
export const usePrefetchBase = <TParameters, TData>(
  getKey: TGetKey<TParameters>,
  queryFunction: (parameters?: TParameters) => Promise<TData>,
  options?: FetchQueryOptions
) => {
  const queryClient = useQueryClient();

  return (parameters?: TParameters) =>
    queryClient.prefetchQuery(
      getKey(parameters),
      () => queryFunction(parameters),
      options
    );
};

// Set Query Data ----------------------------------------------------------------
/**
 * Creates a useSetQueryBase hook that sets data for a query based on the provided parameters.
 * Uses tanstack/react-query's setQueryData from useQueryClient hook.
 * @typeParam TParameters - The type of the parameters used to generate the query key.
 * @typeParam TData - The type of the data returned by the query function.
 * @param getKey - A function that returns a query key based on the provided parameters.
 * @returns A useSetQueryBase hook that sets data for a query based on the provided parameters.
 * @example
 * ```ts
 * const getKey = (parameters?: Partial<BenefitNotificationParameters>) => {
 *    if (isEmpty(parameters)) return [QUERY_KEY_BENEFIT_NOTIFICATIONS];
 *    return [QUERY_KEY_BENEFIT_NOTIFICATIONS, parameters];
 * };
 *
 * const useSetBenefitNotifications = createSetQueryHook<BenefitNotificationParameters, BenefitNotificationsResponse>(getKey);
 *
 * useSetBenefitNotifications({ ...newData }: TData, options);
 *
 * // OR
 *
 * useSetBenefitNotifications((oldData: TData):TData => ({
 *    ...oldData,
 *    ...newData,
 * }), options);
 * ```
 * @see {@link useSetQueryBase}
 */
export const createSetQueryHook = <TParameters, TData>(
  getKey: TGetKey<TParameters>
) => {
  return (options?: SetDataOptions) =>
    useSetQueryBase<TParameters, TData>(getKey, options);
};

/**
 * Sets data for a query based on the provided parameters. It uses tanstack/react-query's useQueryClient hook.
 * @typeParam TParameters - The type of the parameters used to generate the query key.
 * @typeParam TData - The type of the data returned by the query function.
 * @param getKey - A function that returns a query key based on the provided parameters.
 * @param options - The options to use when setting the data for the query.
 * @returns A function that sets data for a query based on the provided parameters.
 * @example
 * ```ts
 * const getKey = (parameters?: Partial<BenefitNotificationParameters>) => {
 *    if (isEmpty(parameters)) return [QUERY_KEY_BENEFIT_NOTIFICATIONS];
 *    return [QUERY_KEY_BENEFIT_NOTIFICATIONS, parameters];
 * };
 *
 * const setBenefitNotifications = useSetQueryBase<BenefitNotificationParameters, BenefitNotificationsResponse>(getKey, options);
 *
 * setBenefitNotifications({ ...newData }: TData, parameters);
 *
 * // OR
 *
 * setBenefitNotifications((oldData: TData):TData => ({
 *    ...oldData,
 *    ...newData,
 * }), parameters);
 * ```
 */
export const useSetQueryBase = <TParameters, TData>(
  getKey: TGetKey<TParameters>,
  options?: SetDataOptions
) => {
  const queryClient = useQueryClient();

  return (
    newData: TData | ((oldData: TData) => TData),
    parameters?: Partial<TParameters>
  ) =>
    queryClient.setQueryData<TData>(
      getKey(parameters),
      (oldData) => {
        if (!oldData) return;

        if (newData instanceof Function) {
          return newData(oldData);
        }

        return {
          ...oldData,
          ...newData,
        };
      },
      options
    );
};
